/*
 * Tiny self-contained toast system. No library — the whole thing is a signal
 * of toast records, a container mounted once in App.tsx, and a global
 * `showToast()` function that pushes/updates records.
 *
 * Update semantics (important for the command lifecycle): a toast is
 * identified by `id`. Calling `showToast` with an already-known id UPDATES the
 * existing record instead of appending a new one. The commands store uses this
 * to walk a single card through Sent → Ack → Executed without stacking three.
 * `correlationId` is a convenience alias so callers can group by correlation
 * without minting their own id.
 */

import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { ToastSuccess, ToastError, ToastWarn, ToastInfo } from '../icons/HvacIcons';
import './Toaster.css';

export type ToastTone = 'info' | 'success' | 'warn' | 'error';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  message?: string;
  /** Auto-hide after N ms of no updates. Undefined = stay until dismissed. */
  autoHideAfterMs?: number;
  /** Convenience marker for command-lifecycle toasts. Not otherwise used. */
  correlationId?: string;
  /** When the toast is currently in a terminal (auto-hide-eligible) state. */
  createdAt: number;
}

const toasts = signal<Toast[]>([]);

/** Per-toast dismissal timer handles, so replacing/updating a toast can cancel
 *  a pending auto-hide before starting a new one. Module-scope map keyed by
 *  toast id. */
const dismissTimers = new Map<string, number>();

function clearTimer(id: string): void {
  const handle = dismissTimers.get(id);
  if (handle !== undefined) {
    window.clearTimeout(handle);
    dismissTimers.delete(id);
  }
}

function scheduleAutoHide(id: string, ms: number): void {
  clearTimer(id);
  const handle = window.setTimeout(() => {
    dismissTimers.delete(id);
    dismissToast(id);
  }, ms);
  dismissTimers.set(id, handle);
}

export interface ShowToastInput {
  id?: string;
  tone: ToastTone;
  title: string;
  message?: string;
  autoHideAfterMs?: number;
  correlationId?: string;
}

/** Default auto-hide fuer Toasts ohne expliziten Wert. Fuenf Sekunden ist
 *  lang genug damit man das Ergebnis liest, kurz genug damit nach einer
 *  Bedienserie nicht ein Turm aus offenen Toasts entsteht. Wer eine Toast
 *  explizit sticky will, uebergibt `autoHideAfterMs: 0`. */
const DEFAULT_AUTO_HIDE_MS = 5000;

/** Push a new toast or update an existing one (matched by `id`, or falling back
 *  to `correlationId`). Returns the toast id — useful for later dismissal.
 *
 *  Auto-hide-Regel: `undefined` → nach {@link DEFAULT_AUTO_HIDE_MS} verschwinden.
 *  `0` → sticky (manuelles Wegklicken noetig, z.B. fuer Write-Fluesse die vom
 *  gleichen Aufrufer per `id` upgedated werden). Positive Zahl → genau die
 *  Dauer. Vor 2026-08-28 war `undefined` = sticky; das hat sich in der
 *  Praxis als nervig herausgestellt, jeder Klick hinterliess einen offenen
 *  Balken bis der User ihn manuell schloss. */
export function showToast(input: ShowToastInput): string {
  const id = input.id ?? input.correlationId ?? crypto.randomUUID();

  const list = toasts.value;
  const existing = list.findIndex((t) => t.id === id);
  const effectiveAutoHide = input.autoHideAfterMs ?? DEFAULT_AUTO_HIDE_MS;
  const next: Toast = {
    id,
    tone: input.tone,
    title: input.title,
    message: input.message,
    autoHideAfterMs: effectiveAutoHide,
    correlationId: input.correlationId,
    createdAt: Date.now(),
  };

  if (existing >= 0) {
    const copy = list.slice();
    copy[existing] = next;
    toasts.value = copy;
  } else {
    toasts.value = [...list, next];
  }

  if (effectiveAutoHide > 0) {
    scheduleAutoHide(id, effectiveAutoHide);
  } else {
    // Sticky (autoHideAfterMs === 0): sicherheitshalber alten Timer killen
    // damit ein vorheriges auto-hide nicht doch noch feuert.
    clearTimer(id);
  }

  return id;
}

export function dismissToast(id: string): void {
  clearTimer(id);
  toasts.value = toasts.value.filter((t) => t.id !== id);
}

/** Mount once from App.tsx. The container is fixed-positioned; nothing about
 *  it participates in normal layout. */
export function Toaster() {
  const list = toasts.value;

  // Safety net: if the tab is hidden for a long time and the timers were
  // throttled, auto-hide anything past its budget on the next visible render.
  useEffect(() => {
    const now = Date.now();
    for (const t of list) {
      if (t.autoHideAfterMs !== undefined && now - t.createdAt > t.autoHideAfterMs) {
        dismissToast(t.id);
      }
    }
  }, [list]);

  if (list.length === 0) return null;

  return (
    <div class="hv-toaster" role="region" aria-label="Benachrichtigungen">
      {list.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastCard({ toast }: { toast: Toast }) {
  return (
    <button
      type="button"
      class={`hv-toast hv-toast--${toast.tone}`}
      onClick={() => dismissToast(toast.id)}
      aria-label={`${toast.title} — schließen`}
    >
      <span class="hv-toast__icon" aria-hidden="true">
        {toast.tone === 'success' && <ToastSuccess size={20} />}
        {toast.tone === 'error' && <ToastError size={20} />}
        {toast.tone === 'warn' && <ToastWarn size={20} />}
        {toast.tone === 'info' && <ToastInfo size={20} running />}
      </span>
      <span class="hv-toast__body">
        <span class="hv-toast__title">{toast.title}</span>
        {toast.message && <span class="hv-toast__message">{toast.message}</span>}
      </span>
    </button>
  );
}
